import { staffSessionHeaders } from '../auth/staffSession';
import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import brand from '../../config/brand';
import { StreamingProviderNotice } from '../../components/StreamingProviderNotice';
import physicalSim from '../../assets/images/physical_sim.png';
import virtualSim from '../../assets/images/virtual_sim.png';
import { useTrackReferralMutation } from '../dashboard/api/activateApi';
import {
  clearActivationProgress,
  loadActivationProgress,
  migratePrepayProgress,
  saveActivationProgress
} from './activationProgress';
import './groupActivation.css';
import { prepayHandoff, savePrepaySelection } from './prepayHandoff';

type Sim = {
  iccid: string;
  activationCode?: string;
  nickname: string;
  confirmed_last4: string;
  sim_type?: 'solar' | 'regular';
  esim_batch?: string;
  state?: string;
  message?: string;
};
type Plan = {
  id: string;
  name: string;
  data_gb: number;
  interval: 'year' | 'month';
  unit_amount: number;
  standard_amount: number;
};
type Order = {
  kind?: string;
  id: string;
  status: string;
  email: string;
  items: Sim[];
  plan: Plan | null;
  checkout_url?: string;
  renewal_at?: number;
  prepay_paid_until?: number;
  prepay_years?: number;
  management?: { action: string; state: string; plan?: Plan; effective_at: number };
  message?: string;
};
type Saved = {
  orderId?: string;
  token?: string;
  items: Sim[];
  email: string;
  emailRoute: 'new' | 'existing' | null;
  signedInForEmail?: boolean;
  step: number;
  planId: string;
  interval: 'year' | 'month';
  plans: Plan[];
  prepayYears: number;
  termChosen?: boolean;
  prepaidOrder?: boolean;
};
const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
export const preview = process.env.REACT_APP_GROUP_PREVIEW === 'true' && isLocal;
const enabled = preview || process.env.REACT_APP_GROUP_ACTIVATION_ENABLED === 'true';
const returningConversionEnabled = preview || process.env.REACT_APP_GROUP_RETURNING_CONVERSION_ENABLED === 'true';
const base = preview
  ? (process.env.REACT_APP_GROUP_PREVIEW_API || 'http://127.0.0.1:5011/activation/groups')
  : `${(process.env.REACT_APP_API_ENDPOINT || '/').replace(/\/$/, '')}/activation/groups`;
const storageKey = preview ? 'farmstream-group-preview-v1' : 'farmstream-group-activation-v1';
const prepayStorageKey = preview ? 'farmstream-prepay-preview-v1' : 'farmstream-prepay-activation-v1';

export function PrepayEntry() {
  const location = useLocation();
  // Old links and existing Stripe sessions keep their order and token, while
  // every customer-facing step now lives on the group URL.
  migratePrepayProgress<Saved>(storageKey, prepayStorageKey, preview, true);
  return <Navigate to={{ pathname: '/activate/group', search: location.search,
    hash: location.hash }} replace />;
}
const blank: Saved = {
  items: [],
  email: '',
  emailRoute: null,
  step: 0,
  planId: 'standard_year',
  interval: 'year',
  plans: [],
  prepayYears: 1,
  prepaidOrder: false
};
const money = (pence: number) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    maximumFractionDigits: pence % 100 ? 2 : 0
  }).format(pence / 100);

function initialSaved(key: string): Saved {
  return { ...blank, prepayYears: key === storageKey ? 1 : 3 };
}

function readSaved(key = storageKey): Saved {
  const persisted = key === storageKey
    ? migratePrepayProgress<Saved & { businessName?: string }>(storageKey, prepayStorageKey, preview)
    : loadActivationProgress<Saved & { businessName?: string }>(key, preview);
  if (!persisted) return initialSaved(key);
  // Discard values saved by earlier versions that collected this on our page.
  delete persisted.businessName;
  // Keep a checkout already in progress open after an upgrade from the older UI.
  if (!persisted.emailRoute && (persisted.step > 0 || persisted.orderId))
    persisted.emailRoute = 'new';
  return { ...initialSaved(key), ...persisted,
    prepayYears: key === storageKey && !persisted.termChosen ? 1
      : persisted.prepayYears ?? initialSaved(key).prepayYears };
}

export async function api(path: string, method = 'GET', body?: unknown, token?: string) {
  let response: Response;
  try {
    response = await fetch(base + path, {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(path.startsWith('/team/') ? staffSessionHeaders() : {}),
        ...((path.startsWith('/conversion/') || path === '/account-route') && !preview && localStorage.getItem('token')
          ? { Authorization: `Bearer ${localStorage.getItem('token')}` } : {}),
        ...(token ? { 'X-Activation-Token': token } : {})
      },
      ...(body === undefined ? {} : { body: JSON.stringify(body) })
    });
  } catch {
    throw new Error(
      'We could not connect to activation. Your SIM list is saved; please try again.'
    );
  }
  if (response.status === 504)
    throw new Error('Checking plans took too long. Your SIM list is saved; please try again.');
  const data = await response
    .json()
    .catch(() => ({ message: 'The activation service is unavailable. Please try again.' }));
  if (!response.ok) {
    const failure = new Error(
      data.message || 'Something went wrong. Your SIM list has been kept. Please try again.'
    ) as Error & { status?: number; simErrors?: Array<{ iccid: string; message: string }> };
    failure.status = response.status;
    if (Array.isArray(data.sim_errors)) failure.simErrors = data.sim_errors;
    throw failure;
  }
  return data;
}

function SimIcon({ small = false }: { small?: boolean }) {
  return (
    <svg
      width={small ? 20 : 28}
      height={small ? 24 : 32}
      viewBox="0 0 28 32"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M6 2h11l7 7v19a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2Z"
        stroke="currentColor"
        strokeWidth="1.7"
      />
      <rect x="8" y="13" width="12" height="11" rx="2" stroke="currentColor" strokeWidth="1.4" />
      <path d="M12 13v11m4-11v11M8 18h12" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  );
}

function PlanIcon({ name }: { name: string }) {
  const light = name === 'Light';
  const heavy = name === 'Heavy';
  return (
    <svg width="38" height="38" viewBox="0 0 44 44" fill="none" aria-hidden="true">
      <g stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        {light ? (
          <>
            <path d="M16 13c-2-4 2-7 6-5 4-2 8 1 6 5m-12 0c-4-3-7 0-4 3l5 1m11-4c4-3 7 0 4 3l-5 1" />
            <path d="M17 13c3-2 7-2 10 0v8c0 5-10 5-10 0v-8Zm3 5h.01m4 0h.01m-3 4h2" />
            <path d="M13 19c-5 1-5 7-1 8-1 4 3 6 6 4 2 3 6 3 8 0 3 2 7 0 6-4 4-1 4-7-1-8m-15 13v5h4v-4m4 0v4h4v-5" />
          </>
        ) : heavy ? (
          <>
            <path d="M17 11c2-4 8-4 10 0l-1 10c-1 5-7 5-8 0l-1-10Zm3 5h.01m4 0h.01m-3 5h2" />
            <path d="M17 12C15 5 6 6 6 13c0 7 10 7 10 1 0-3-5-3-5 0m16-2c2-7 11-6 11 1 0 7-10 7-10 1 0-3 5-3 5 0" />
            <path d="M12 20c-7 2-7 10-1 12-1 5 5 7 8 4 2 2 4 2 6 0 3 3 9 1 8-4 6-2 6-10-1-12m-17 17v4h4v-4m6 0v4h4v-4" />
          </>
        ) : (
          <>
            <path d="M15 11c0-4 6-6 9-3 4-2 9 1 9 5 5 0 7 6 3 9 2 4-2 8-6 7-3 3-8 2-9-1-4 2-9-1-9-5" />
            <path d="M10 15c-4-4-8-1-6 3l5 1m7-4c4-4 8-1 6 3l-5 1" />
            <path d="M10 14c2-2 5-2 7 0l1 7c0 4-8 5-9 0l1-7Z" />
            <path d="M12 19h.01m3 0h.01m-2 3h1m9 6v7h4v-7m5-1v8h4v-9" />
          </>
        )}
      </g>
    </svg>
  );
}

export const GroupActivation = () => {
  const progressKey = storageKey;
  const [trackReferral] = useTrackReferralMutation();
  const [saved, setSaved] = useState<Saved>(() => readSaved(progressKey));
  const prepaid = saved.prepaidOrder === true;
  const [order, setOrder] = useState<Order | null>(null);
  const [code, setCode] = useState('');
  const [codeMode, setCodeMode] = useState<'activation' | 'iccid'>('activation');
  const [candidate, setCandidate] = useState<Sim | null>(null);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState('');
  const [pollError, setPollError] = useState('');
  const [codeError, setCodeError] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyMessage, setBusyMessage] = useState('');
  const [ready, setReady] = useState(false);
  const [checkoutEnabled, setCheckoutEnabled] = useState(preview);
  const [prepayAvailable, setPrepayAvailable] = useState(preview);
  const [startedCheckout, setStartedCheckout] = useState(false);
  const [showEntry, setShowEntry] = useState(() => saved.items.length === 0);
  const [emailError, setEmailError] = useState('');
  const [prepayHandoffError, setPrepayHandoffError] = useState('');
  const input = useRef<HTMLInputElement>(null);
  const emailInput = useRef<HTMLInputElement>(null);
  const title = useRef<HTMLHeadingElement>(null);
  const selected = saved.plans.find((p) => p.id === saved.planId);
  const multiYearPlan = saved.plans.find((p) => p.interval === 'year' && p.name === selected?.name)
    || saved.plans.find((p) => p.interval === 'year' && p.name === 'Standard')
    || saved.plans.find((p) => p.interval === 'year');
  const locked = !!order && !['draft'].includes(order.status);
  const payment = locked && ['checkout', 'creating_checkout'].includes(order!.status);
  const progress = locked && !payment;
  const polling = locked && ['checkout', 'creating_checkout', 'activating'].includes(order!.status);
  const activeStep = locked ? 3 : saved.step > 0 ? saved.step + 1 : saved.emailRoute === 'new' ? 1 : 0;
  const count = saved.items.length;
  const minimumSims = 1;
  const codeComplete = codeMode === 'activation' ? /^[A-Z]{4}$/.test(code) : /^\d{19,20}$/.test(code);
  const simTypes = new Set((order?.items || saved.items).map((sim) => sim.sim_type));
  const simTypeLabel = simTypes.size > 1 ? 'Solar and regular' : simTypes.has('regular') ? 'Regular UK' : 'Solar';
  const pendingChange = order?.management?.state !== 'complete' ? order?.management : undefined;
  const billingCount = progress
    ? Math.max(
        0,
        (order?.items.filter(
          (sim) => !['refunded', 'removed', 'canceled'].includes(sim.state || '')
        ).length || 0) - (pendingChange?.action === 'remove' ? 1 : 0)
      )
    : count;
  const billingPlan = progress ? pendingChange?.plan || order?.plan || selected : selected;
  const annualSaving = (plan: Plan) => {
    const monthly = saved.plans.find(
      (candidate) => candidate.name === plan.name && candidate.interval === 'month'
    );
    return monthly ? Math.max(0, monthly.unit_amount * 12 - plan.unit_amount) : 0;
  };
  const monthlyEquivalent = (plan: Plan) =>
    saved.plans.find((candidate) => candidate.name === plan.name && candidate.interval === 'month');

  useEffect(() => {
    saveActivationProgress(progressKey, preview, saved);
  }, [saved, progressKey]);
  useEffect(() => {
    if (ready) title.current?.focus();
  }, [activeStep, order?.status]);
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    (async () => {
      try {
        const config = await api('/config');
        if (config.preview !== preview)
          throw new Error('The preview and payment service do not match. Checkout is disabled.');
        if (!cancelled) {
          setCheckoutEnabled(config.checkout_enabled === true && (!prepaid || config.prepay_enabled === true));
          setPrepayAvailable(config.prepay_enabled === true);
        }
        if (saved.orderId && saved.token) {
          let current: Order;
          try {
            current = await api(`/orders/${saved.orderId}`, 'GET', undefined, saved.token);
          } catch (failure) {
            if ((failure as Error & { status?: number }).status !== 404) throw failure;
            if (!cancelled) {
              setSaved((s) => ({ ...s, orderId: undefined, token: undefined, step: 0 }));
              setError('Your saved activation has expired. Your SIM list is still here; continue to check it again.');
              setReady(true);
            }
            return;
          }
          if ((current.kind === 'prepaid') !== prepaid)
            throw new Error('This saved activation belongs to a different checkout. Please start again.');
          if (!cancelled) {
            setOrder(current);
            if (current.status !== 'draft')
              setSaved((s) => ({
                ...s,
                email: current.email,
                items: current.items.map((sim: Sim) => ({
                  ...sim,
                  activationCode: s.items.find((item) => item.iccid === sim.iccid)?.activationCode
                })),
                plans: current.plan
                  ? s.plans.some((plan) => plan.id === current.plan!.id)
                    ? s.plans.map((plan) => plan.id === current.plan!.id ? current.plan! : plan)
                    : [...s.plans, current.plan]
                  : s.plans,
                planId: current.plan?.id || s.planId,
                interval: current.plan?.interval || s.interval,
                prepayYears: current.prepay_years || s.prepayYears
              }));
            // Repair progress written by older releases that kept only the
            // checkout's selected plan instead of the full catalogue.
            if (current.status === 'draft' && current.plan && saved.step >= 1 && saved.items.length && saved.plans.length < 3)
              await saveOrder(current.plan.id);
          }
        }
        if (!cancelled) setReady(true);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!saved.orderId || !saved.token || !polling) return;
    let stopped = false;
    const timer = window.setInterval(async () => {
      try {
        const current = await api(`/orders/${saved.orderId}`, 'GET', undefined, saved.token);
        if (!stopped) {
          setOrder(current);
          setPollError('');
          if (current.status === 'draft') {
            setSaved((s) => ({ ...s, step: 2 }));
            setStartedCheckout(false);
            if (current.message) setError(current.message);
          }
        }
      } catch (e) {
        if (!stopped)
          setPollError(
            'We’re reconnecting to your activation. Your progress is saved; please don’t pay again.'
          );
      }
    }, 2500);
    return () => {
      stopped = true;
      window.clearInterval(timer);
    };
  }, [saved.orderId, saved.token, polling]);

  useEffect(() => {
    if (startedCheckout && !preview && order?.status === 'checkout' && order.checkout_url)
      window.location.assign(order.checkout_url);
  }, [startedCheckout, order?.status, order?.checkout_url]);

  useEffect(() => {
    const returnedFromCheckout = (event: PageTransitionEvent) => {
      // Browser Back may restore this page from its memory cache, including
      // the old Opening Stripe spinner. It is a review page again on return.
      if (event.persisted) setStartedCheckout(false);
    };
    window.addEventListener('pageshow', returnedFromCheckout);
    return () => window.removeEventListener('pageshow', returnedFromCheckout);
  }, []);

  async function run(fn: () => Promise<void>, message = 'Working on your activation…') {
    setBusy(true);
    setBusyMessage(message);
    setError('');
    setSaved((s) => ({ ...s, items: s.items.map((item) => ({ ...item, message: undefined })) }));
    try {
      await fn();
    } catch (e) {
      const failure = e as Error & { simErrors?: Array<{ iccid: string; message: string }> };
      if (failure.simErrors?.length) {
        setSaved((s) => ({
          ...s,
          items: s.items.map((item) => ({
            ...item,
            message: failure.simErrors?.find((entry) => entry.iccid === item.iccid)?.message
          }))
        }));
      } else {
        setError(failure.message);
      }
    } finally {
      setBusy(false);
      setBusyMessage('');
    }
  }

  async function validate(e: React.FormEvent) {
    e.preventDefault();
    setCodeError('');
    const enteredCode = code.trim().toUpperCase().replace(/\s/g, '');
    if (codeMode === 'activation' && !/^[A-Z]{4}$/.test(enteredCode)) {
      setCodeError('Enter the four-letter activation code printed on your SIM or camera.');
      return;
    }
    if (codeMode === 'iccid' && !/^\d{19,20}$/.test(enteredCode)) {
      setCodeError('Enter the 19–20 digit ICCID printed on your SIM or camera.');
      return;
    }
    setBusy(true);
    setBusyMessage('Checking your SIM…');
    try {
      const sim = await api('/validate', 'POST', { code: enteredCode });
      if (saved.items.some((i) => i.iccid === sim.iccid))
        throw new Error('This SIM is already on your list. Add a different one.');
      setCandidate({
        ...sim,
        activationCode: /^[A-Z]{4}$/.test(enteredCode) ? enteredCode : undefined,
        nickname: ''
      });
      setConfirmed(false);
    } catch (e) {
      setCodeError((e as Error).message);
    } finally {
      setBusy(false);
      setBusyMessage('');
    }
  }

  async function checkEmail(e: React.FormEvent) {
    e.preventDefault();
    const email = saved.email.trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailError('Enter a valid email address to continue.');
      emailInput.current?.focus();
      return;
    }
    setBusy(true);
    setBusyMessage('Checking your account…');
    setEmailError('');
    try {
      const result = await api('/account-route', 'POST', { email });
      if (result.route !== 'new' && result.route !== 'existing')
        throw new Error('We could not check this account. Please try again.');
      if (result.route === 'existing')
        sessionStorage.setItem('farmstream-group-returning-email', email);
      else
        sessionStorage.removeItem('farmstream-group-returning-email');
      setSaved((s) => ({ ...s, email, emailRoute: result.route,
        signedInForEmail: result.signed_in_for_email === true }));
    } catch (failure) {
      setEmailError((failure as Error).message);
    } finally {
      setBusy(false);
      setBusyMessage('');
    }
  }

  async function saveOrder(planId?: string) {
    let orderId = saved.orderId,
      token = saved.token;
    if (!orderId || !token) {
      const created = await api(prepaid ? '/prepay/orders' : '/orders', 'POST', {});
      orderId = created.order.id;
      token = created.access_token;
      setSaved((s) => ({ ...s, orderId, token }));
      const ref =
        new URLSearchParams(window.location.search).get('ref') || localStorage.getItem('trade_ref');
      if (!preview && ref) trackReferral({ ref, email: saved.email, iccid: saved.items[0]?.iccid });
    }
    const result = await api(
      `/orders/${orderId}`,
      'PUT',
      {
        email: saved.email.trim(),
        region: 'uk',
        items: saved.items,
        plan_id: planId,
        ...(prepaid ? { prepay_years: saved.prepayYears } : {}),
        referral:
          new URLSearchParams(window.location.search).get('ref') ||
          localStorage.getItem('trade_ref') ||
          ''
      },
      token
    );
    setOrder(result.order);
    setSaved((s) => ({ ...s, orderId, token, plans: result.plans,
      planId: result.plans.some((p: Plan) => p.id === s.planId)
        ? s.planId : result.plans.find((p: Plan) => p.name === 'Standard')?.id || result.plans[0]?.id || s.planId }));
    return result;
  }

  function addCandidate() {
    if (!candidate || !confirmed || count >= 10) return;
    setSaved((s) => ({ ...s, items: [...s.items, candidate] }));
    setCandidate(null);
    setCode('');
    setConfirmed(false);
    setShowEntry(false);
    setCodeError('');
  }

  async function continueWithPrepay() {
    const existing = loadActivationProgress<Saved>(prepayStorageKey, preview);
    if (!multiYearPlan) {
      setPrepayHandoffError('We could not find an annual plan for these SIMs. Please try again.');
      return;
    }
    const transfer: Saved = {
      ...blank,
      email: saved.email,
      emailRoute: 'new',
      signedInForEmail: saved.signedInForEmail,
      items: saved.items.map(({ message, ...sim }) => sim),
      step: 1,
      planId: multiYearPlan.id,
      plans: saved.plans.filter((plan) => plan.interval === 'year'),
      prepayYears: saved.prepayYears,
      prepaidOrder: true,
      termChosen: true
    };
    const selection = { ...transfer, offer: multiYearPlan };
    const handoff = await prepayHandoff(existing, selection, api);
    if (handoff.resumeOnly) {
      const current = await api(`/orders/${handoff.orderId}`, 'GET', undefined, handoff.token);
      setSaved({ ...existing!, prepaidOrder: true, termChosen: true, step: 2 });
      setOrder(current);
      clearActivationProgress(prepayStorageKey, preview);
      return;
    }
    transfer.orderId = handoff.orderId;
    transfer.token = handoff.token;
    const ref = new URLSearchParams(window.location.search).get('ref') || localStorage.getItem('trade_ref');
    if (!preview && ref) trackReferral({ ref, email: transfer.email, iccid: transfer.items[0]?.iccid });
    const result = await savePrepaySelection(handoff, selection, api, ref || '');
    transfer.plans = result.plans;
    transfer.planId = result.order.plan.id;
    transfer.step = 2;
    saveActivationProgress(storageKey, preview, transfer);
    const restored = loadActivationProgress<Saved>(storageKey, preview);
    if (restored?.email !== transfer.email || restored.items?.length !== transfer.items.length || restored.step !== 2) {
      setPrepayHandoffError('We could not carry your SIM list to the multi-year page. Please try again.');
      return;
    }
    setOrder(result.order);
    setSaved(transfer);
    clearActivationProgress(prepayStorageKey, preview);
  }

  async function chooseMonthly() {
    if (!prepaid) {
      setSaved((s) => ({ ...s, interval: 'month',
        planId: s.plans.find((p) => p.name === selected?.name && p.interval === 'month')?.id || s.planId }));
      return;
    }
    // A draft can change payment type without making the customer restart.
    // Keep the previous order on the server; unpaid drafts never hold a SIM.
    const created = await api('/orders', 'POST', {});
    const result = await api(`/orders/${created.order.id}`, 'PUT', {
      email: saved.email.trim(), region: 'uk', items: saved.items
    }, created.access_token);
    const monthly = result.plans.find((p: Plan) => p.name === selected?.name && p.interval === 'month');
    if (!monthly) throw new Error('Monthly billing is unavailable for this plan. Your selection is saved.');
    setOrder(result.order);
    setSaved((s) => ({ ...s, prepaidOrder: false, interval: 'month', orderId: created.order.id,
      token: created.access_token, plans: result.plans, planId: monthly.id }));
  }

  async function changePlan() {
    setStartedCheckout(false);
    if (payment) {
      const current = await api(`/orders/${saved.orderId}/cancel-checkout`, 'POST', {}, saved.token);
      setOrder(current);
      // Payment may have completed while the customer was returning. Keep its
      // progress visible instead of reopening a paid order for editing.
      if (current.status !== 'draft') return;
    }
    await saveOrder(saved.planId);
    setSaved((s) => ({ ...s, step: 1 }));
  }

  const startOver = () =>
    run(async () => {
      if (preview) await api('/preview-reset', 'POST', {});
      clearActivationProgress(progressKey, preview);
      sessionStorage.removeItem('farmstream-group-returning-email');
      setSaved({ ...initialSaved(progressKey), items: [], plans: [] });
      setOrder(null);
      setCode('');
      setCodeMode('activation');
      setCandidate(null);
      setShowEntry(true);
      setEmailError('');
      setReady(true);
      setPollError('');
      window.history.replaceState({}, '', window.location.pathname);
    });

  if (!enabled || (prepaid && ready && !prepayAvailable && !locked))
    return (
      <main className="ga ga-unavailable">
        <img src={brand.logo} alt={brand.displayName} />
        <h1>{prepaid ? 'Prepaid activation is coming soon' : 'Group activation is coming soon'}</h1>
        <p>{prepaid ? 'Please use standard activation while this payment option is being prepared.'
          : 'You can still activate your SIMs individually.'}</p>
        <a href="/activate">Activate a SIM →</a>
      </main>
    );

  return (
    <div className="ga">
      {preview && (
        <div className="ga-preview">
          <span>
            <strong>LOCAL PREVIEW</strong> Test SIMs and simulated payments. No charges or real
            activations.
          </span>
          <button onClick={startOver} disabled={busy}>
            Reset test
          </button>
        </div>
      )}
      <main className="ga-main">
        <div className="ga-brand-title">
          <img src={brand.logo} alt={brand.displayName} />
          <div>
            <h1>Activate multiple SIMs</h1>
          </div>
        </div>
        <ol className="ga-steps" aria-label="Activation progress">
          {['Email', 'Add SIMs', 'Choose plan', 'Review'].map((label, index) => (
            <li
              key={label}
              aria-current={index === activeStep ? 'step' : undefined}
              className={index <= activeStep ? 'ga-step-current' : ''}
            >
              <span>{index < activeStep ? '✓' : index + 1}</span>
              <b>{label}</b>
            </li>
          ))}
        </ol>
        {((busy && busyMessage) || (!ready && !error)) && (
          <div className="ga-working" role="status" aria-live="polite">
            <span className="ga-spinner" aria-hidden="true" />
            {busy ? busyMessage : 'Connecting to activation service…'}
          </div>
        )}
        {error && (
          <div className="ga-error ga-page-error" role="alert">
            {error}
            {!ready && <button onClick={() => window.location.reload()}>Try again</button>}
          </div>
        )}
        {pollError && (
          <div className="ga-error ga-page-error" role="status">
            {pollError}
          </div>
        )}
        <div className={`ga-columns ${!progress ? 'ga-columns-single' : ''} ${!progress && saved.step === 0 ? 'ga-entry-layout' : ''} ${!progress && saved.step === 1 ? 'ga-plan-layout' : ''}`}>
          <section className="ga-panel" aria-busy={busy}>
            {!locked && saved.step === 0 && (
              <>
                <div className="ga-section-heading">
                  <span className="ga-icon">
                    <SimIcon />
                  </span>
                  <div>
                    <h2 ref={title} tabIndex={-1}>
                      {saved.emailRoute === 'new' ? 'Enter your SIM codes' : 'Start with your email'}
                    </h2>
                    <p>{saved.emailRoute === 'new'
                      ? 'Add a SIM for each camera you want on this plan. Yearly multi-SIM discounts start at 3 SIMs.'
                      : 'We’ll find the right way to add your SIMs.'}</p>
                  </div>
                </div>
                {!saved.emailRoute && (
                  <form className="ga-email-step ga-email-first" onSubmit={checkEmail}>
                    <label className="ga-label" htmlFor="ga-email">Email address</label>
                    <input
                      ref={emailInput}
                      className="ga-input"
                      id="ga-email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      value={saved.email}
                      aria-invalid={!!emailError}
                      aria-describedby={emailError ? 'ga-email-error' : 'ga-email-help'}
                      onChange={(e) => {
                        setSaved((s) => ({ ...s, email: e.target.value }));
                        setEmailError('');
                      }}
                    />
                    <p className="ga-hint" id="ga-email-help">We’ll use this for your plan and receipt.</p>
                    {emailError && <p className="ga-error" id="ga-email-error" role="alert">{emailError}</p>}
                    <button className="ga-button" type="submit" disabled={!ready || busy}>
                      {busy ? 'Checking…' : 'Continue →'}
                    </button>
                  </form>
                )}
                {saved.emailRoute === 'existing' && (
                  <div className="ga-existing-account">
                    <h3>Add SIMs to your current plan</h3>
                    {!prepaid && returningConversionEnabled ? <>
                      <p>{preview ? 'See the sample existing cameras and combined price.'
                        : `Sign in with ${saved.email}. The email link will bring you straight back to adding SIMs.`}</p>
                      <a className="ga-button" href={preview || saved.signedInForEmail
                        ? '/activate/group/add'
                        : '/login?redirect=%2Factivate%2Fgroup%2Fadd&switch=1'}>
                        {preview || saved.signedInForEmail ? 'Continue to add SIMs →' : 'Email me a sign-in link →'}
                      </a>
                    </> : <>
                      <p>Adding SIMs to an existing plan is handled by our team for now. Your current plan will not change here.</p>
                      <a className="ga-button" href="mailto:support@farmstream.co.uk?subject=Add%20SIMs%20to%20my%20plan">
                        Ask us to add SIMs →
                      </a>
                    </>}
                    <button className="ga-back" onClick={() => setSaved((s) => ({ ...s, emailRoute: null }))}>
                      Use a different email
                    </button>
                  </div>
                )}
                {saved.emailRoute === 'new' && <>
                {count > 1 && <div className="ga-linked-note">
                  <strong>One plan for all your SIMs</strong>
                  <p>They share data and a renewal date, and can’t be paused separately.</p>
                </div>}
                {preview && (
                  <details className="ga-demo">
                    <summary>Test codes to try</summary>
                    <p className="ga-demo-primary">
                      Start with{' '}
                      <button
                        onClick={() => {
                          setCode('FARM');
                          setCodeMode('activation');
                          setShowEntry(true);
                          setCandidate(null);
                        }}
                      >
                        FARM
                      </button>{' '}
                      and{' '}
                      <button
                        onClick={() => {
                          setCode('BARN');
                          setCodeMode('activation');
                          setShowEntry(true);
                          setCandidate(null);
                        }}
                      >
                        BARN
                      </button>
                      . Both work normally.
                    </p>
                    <p className="ga-demo-secondary">
                      More examples: REGU and REGX (regular UK), GATE (eSIM), USED (unavailable),
                      SLOW (retry), FAIL (needs support). For extra SIMs, use ICCIDs ending
                      1006–1010. Reset the test to reuse codes.
                    </p>
                  </details>
                )}
                {count > 0 && (
                  <div className="ga-sim-list" aria-label="Your SIMs">
                    {saved.items.map((sim, index) => (
                      <div className="ga-sim" key={sim.iccid}>
                        <div className="ga-sim-top">
                          <span className="ga-sim-symbol">
                            <SimIcon small />
                          </span>
                          <div>
                            <strong>
                              {sim.activationCode
                                ? `SIM ${sim.activationCode}`
                                : `SIM ending ${sim.iccid.slice(-4)}`}
                            </strong>
                            <span className="ga-valid">
                              ✓ {sim.sim_type === 'regular' ? 'Regular UK SIM' : 'Solar SIM'}{' '}
                              checked
                            </span>
                          </div>
                          <button
                            className="ga-remove"
                            aria-label={`Remove SIM ending ${sim.iccid.slice(-4)}`}
                            onClick={() => {
                              setSaved((s) => ({
                                ...s,
                                items: s.items.filter((i) => i.iccid !== sim.iccid)
                              }));
                              setShowEntry(count - 1 < minimumSims);
                            }}
                          >
                            Remove
                          </button>
                        </div>
                        <div className="ga-sim-number">
                          <span>ICCID</span>
                          <span>{sim.iccid}</span>
                        </div>
                        {sim.message && (
                          <p className="ga-error" role="alert">
                            {sim.message}
                          </p>
                        )}
                        <details className="ga-nickname" open={Boolean(sim.nickname)}>
                          <summary>
                            {sim.nickname ? `Camera nickname: ${sim.nickname}` : 'Give this camera a nickname (optional)'}
                          </summary>
                          <label className="ga-label" htmlFor={`nickname-${index}`}>
                            Camera nickname
                          </label>
                          <input
                            className="ga-input"
                            id={`nickname-${index}`}
                            maxLength={80}
                            placeholder={index === 0 ? 'e.g. Lambing shed' : 'e.g. Field gate'}
                            value={sim.nickname}
                            onChange={(e) =>
                              setSaved((s) => ({
                                ...s,
                                items: s.items.map((i) =>
                                  i.iccid === sim.iccid ? { ...i, nickname: e.target.value } : i
                                )
                              }))
                            }
                          />
                        </details>
                        {sim.esim_batch && (
                          <p className="ga-hint">
                            This eSIM needs a one-time camera setting update. We’ll show you how
                            after activation.
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {(showEntry || count === 0) && !candidate && count < 10 && (
                  <form onSubmit={validate} className="ga-code-form">
                    <div className="ga-code-method">
                      <div
                        className="ga-code-modes"
                        role="group"
                        aria-label="Choose how to enter your SIM"
                      >
                        <button
                          type="button"
                          aria-pressed={codeMode === 'activation'}
                          onClick={() => {
                            setCodeMode('activation');
                            setCode('');
                            setCandidate(null);
                            setCodeError('');
                            input.current?.focus();
                          }}
                        >
                          4-letter code
                        </button>
                        <button
                          type="button"
                          aria-pressed={codeMode === 'iccid'}
                          onClick={() => {
                            setCodeMode('iccid');
                            setCode('');
                            setCandidate(null);
                            setCodeError('');
                            input.current?.focus();
                          }}
                        >
                          Full SIM number
                        </button>
                      </div>
                    </div>
                    <label className="ga-label" htmlFor="ga-code">
                      {codeMode === 'activation'
                        ? count
                          ? 'Next SIM code'
                          : 'First SIM code'
                        : count
                          ? 'Next SIM ICCID'
                          : 'SIM ICCID'}
                    </label>
                    <div className="ga-input-action">
                      <input
                        ref={input}
                        className={`ga-input ga-code ${codeMode === 'activation' ? 'ga-code-activation' : ''}`}
                        id="ga-code"
                        autoComplete="off"
                        autoCapitalize={codeMode === 'activation' ? 'characters' : 'off'}
                        inputMode={codeMode === 'iccid' ? 'numeric' : 'text'}
                        spellCheck={false}
                        placeholder={
                          codeMode === 'activation' ? 'e.g. FARM' : '19–20 digit SIM number'
                        }
                        aria-describedby={codeError ? 'ga-code-error' : 'ga-code-help'}
                        aria-invalid={!!codeError}
                        value={code}
                        onChange={(e) => {
                          setCode(
                            codeMode === 'activation'
                              ? e.target.value.replace(/[^a-zA-Z]/g, '').toUpperCase().slice(0, 4)
                              : e.target.value.replace(/\D/g, '').slice(0, 20)
                          );
                          setCandidate(null);
                          setConfirmed(false);
                          setCodeError('');
                        }}
                      />
                      <button
                        className="ga-button ga-button-secondary"
                        disabled={!ready || busy || !codeComplete}
                        type="submit"
                      >
                        {busy ? 'Checking…' : 'Check SIM'}
                      </button>
                    </div>
                    <p className="ga-hint ga-code-help" id="ga-code-help">
                      <span>
                        {codeMode === 'activation'
                          ? 'Find the four-letter code on your SIM card or camera.'
                          : 'Use the full SIM number (ICCID) from your SIM card or camera.'}
                      </span>
                      <strong aria-live="polite">
                        {codeMode === 'activation'
                          ? `${code.length} of 4 letters`
                          : `${code.length} of 19–20 digits`}
                      </strong>
                    </p>
                    <details className="ga-find-code">
                      <summary>Where do I find the code or SIM number?</summary>
                      <div className="ga-find-code-examples">
                        <figure>
                          <img src={physicalSim} alt="SIM card with its four-letter activation code and ICCID marked" />
                          <figcaption>Physical SIM: both are printed on the card.</figcaption>
                        </figure>
                        <figure>
                          <img src={virtualSim} alt="Camera label with the ICCID marked" />
                          <figcaption>Built-in SIM: look for the ICCID on your camera label.</figcaption>
                        </figure>
                      </div>
                    </details>
                    {codeError && (
                      <p className="ga-error" role="alert" id="ga-code-error">
                        {codeError}
                      </p>
                    )}
                    {count >= minimumSims && (
                      <button
                        type="button"
                        className="ga-back ga-done-adding"
                        onClick={() => {
                          setShowEntry(false);
                          setCode('');
                          setCodeError('');
                        }}
                      >
                        Done adding SIMs
                      </button>
                    )}
                  </form>
                )}
                {candidate && (
                  <div className="ga-confirm">
                    <strong>Is this your SIM?</strong>
                    <p>
                      Check the last four digits on your SIM card or camera.
                    </p>
                    <div className="ga-digits">{candidate.iccid.slice(-4)}</div>
                    <label className="ga-check">
                      <input
                        type="checkbox"
                        checked={confirmed}
                        onChange={(e) => setConfirmed(e.target.checked)}
                      />
                      <span>Yes, my SIM ends in {candidate.iccid.slice(-4)}.</span>
                    </label>
                    <div className="ga-confirm-actions">
                      <button className="ga-button" disabled={!confirmed} onClick={addCandidate}>
                        Add this SIM
                      </button>
                      <button
                        type="button"
                        className="ga-back ga-confirm-retry"
                        onClick={() => {
                          setCandidate(null);
                          setConfirmed(false);
                          setCode('');
                          setShowEntry(true);
                          window.setTimeout(() => input.current?.focus(), 0);
                        }}
                      >
                        Digits don’t match? Try another code
                      </button>
                    </div>
                  </div>
                )}
                {!candidate && !showEntry && count > 0 && count < 10 && (
                  <button
                    className="ga-add"
                    onClick={() => {
                      setShowEntry(true);
                      window.setTimeout(() => input.current?.focus(), 0);
                    }}
                  >
                    <span aria-hidden="true">＋</span> Add another SIM
                  </button>
                )}
                {count === 10 && (
                  <p className="ga-note">You’ve added the maximum of 10 SIMs for one activation.</p>
                )}
                <div className="ga-footer ga-sims-footer">
                  <button className="ga-back" onClick={() => setSaved((s) => ({ ...s, emailRoute: null }))}>
                    ← Back to email
                  </button>
                  {count > 0 && <span>{count} {count === 1 ? 'SIM' : 'SIMs'} added</span>}
                  {count >= minimumSims && !showEntry && !candidate && (
                    <button
                      className="ga-button"
                      disabled={!ready || busy}
                      onClick={() => run(async () => {
                        await saveOrder();
                        setSaved((s) => ({ ...s, step: 1 }));
                      }, 'Checking plans for all your SIMs…')}
                    >
                      {busy ? 'Checking plans…' : 'Choose a plan'} <span aria-hidden="true">→</span>
                    </button>
                  )}
                </div>
                </>}
              </>
            )}

            {!locked && saved.step === 1 && (
              <>
                <h2 ref={title} tabIndex={-1}>
                  {count === 1 ? 'Choose your SIM plan' : `Choose one plan for all ${count} SIMs`}
                </h2>
                <p className="ga-plan-intro">
                  {count === 1 ? 'Choose how much data your camera needs each month.'
                    : `Data is pooled and shared across all ${count} cameras.`}
                </p>
                <div className="ga-billing-choice">
                  <span className="ga-billing-label" id="ga-billing-label">How often would you like to pay?</span>
                  <div className="ga-billing-toggle" role="group" aria-labelledby="ga-billing-label">
                    <button
                      type="button"
                      disabled={busy}
                      aria-pressed={saved.interval === 'year'}
                      onClick={() =>
                        setSaved((s) => ({
                          ...s,
                          interval: 'year',
                          planId: s.plans.find((p) => p.name === s.plans.find((current) => current.id === s.planId)?.name && p.interval === 'year')?.id || s.planId
                        }))
                      }
                    >
                      Yearly
                    </button>
                    <button
                      type="button"
                      disabled={busy}
                      aria-pressed={saved.interval === 'month'}
                      onClick={() => run(chooseMonthly, 'Updating billing options…')}
                    >
                      Monthly
                    </button>
                  </div>
                </div>
                <div className="ga-plans">
                  {saved.plans
                    .filter((p) => p.interval === saved.interval)
                    .map((p) => {
                      const monthly = monthlyEquivalent(p);
                      const saving = annualSaving(p);
                      const pooledGb = p.data_gb * count;
                      const termYears = prepaid || (p.interval === 'year' && prepayAvailable && checkoutEnabled)
                        ? saved.prepayYears : 1;
                      return (
                        <button
                          key={p.id}
                          className={`ga-plan ${saved.planId === p.id ? 'ga-plan-selected' : ''}`}
                          aria-pressed={saved.planId === p.id}
                          onClick={() => setSaved((s) => ({ ...s, planId: p.id }))}
                        >
                          <div className="ga-plan-title">
                            <span className="ga-plan-name">
                              <span className="ga-plan-icon">
                                <PlanIcon name={p.name} />
                              </span>
                              <strong>{p.name}</strong>
                            </span>
                            <span className="ga-radio">{saved.planId === p.id ? '●' : '○'}</span>
                          </div>
                          <span className="ga-plan-hours">
                            <strong>
                              ≈ {pooledGb}–{pooledGb * 2} hours
                            </strong>
                            <small>of shared streaming each month</small>
                          </span>
                          <span className="ga-plan-pool">{pooledGb} GB pooled each month</span>
                          <span className="ga-plan-price">
                            {money(p.unit_amount * count * termYears)}
                            <small> {prepaid || termYears > 1 ? 'pay today' : `/ ${p.interval} total`}</small>
                          </span>
                          <span className="ga-plan-total">
                            {money(p.unit_amount)} per SIM / {p.interval} · {count} {count === 1 ? 'SIM' : 'SIMs'}
                          </span>
                          {!prepaid && p.interval === 'year' && monthly && saving > 0 && (
                            <span className="ga-saving-badge">Save {money(saving * count * termYears)}{count < 3 ? ' vs monthly' : ''}</span>
                          )}
                        </button>
                      );
                    })}
                </div>
                {prepayHandoffError && <p className="ga-error" role="alert">
                  {prepayHandoffError}
                </p>}
                <p className="ga-hint">
                  {!prepaid && saved.interval === 'year' && saved.prepayYears > 1 &&
                    'Prepaying more years adds no extra discount; your plan renews yearly afterward. '}
                  Prices include VAT; streaming times are estimates.
                </p>
                <div className="ga-footer ga-plan-footer">
                  <button className="ga-back" onClick={() => setSaved((s) => ({ ...s, step: 0 }))}>
                    ← Back to SIMs
                  </button>
                  <div className="ga-plan-footer-actions">
                    {saved.interval === 'year' && prepayAvailable && checkoutEnabled && (
                      <div className="ga-group-term">
                        <label htmlFor="ga-group-years">Pay upfront for</label>
                        <div className="ga-prepay-select">
                          <select id="ga-group-years" value={saved.prepayYears}
                            onChange={(e) => {
                              setSaved((s) => ({ ...s, prepayYears: Number(e.target.value), termChosen: true }));
                              setPrepayHandoffError('');
                            }}>
                            {[1, 2, 3, 4, 5].map((years) =>
                              <option key={years} value={years}>{years} {years === 1 ? 'year' : 'years'}</option>)}
                          </select>
                        </div>
                        <small>Pay up to 5 years upfront if it helps with a grant or committee approval.</small>
                      </div>
                    )}
                    <button
                      className="ga-button"
                      disabled={busy || !selected}
                      onClick={() => {
                        if (!prepaid && saved.interval === 'year' && saved.prepayYears > 1 && prepayAvailable && checkoutEnabled) {
                          run(continueWithPrepay, 'Preparing your review…');
                          return;
                        }
                        run(async () => {
                          await saveOrder(saved.planId);
                          setSaved((s) => ({ ...s, step: 2 }));
                        }, 'Preparing your review…');
                      }}
                    >
                      {busy ? 'Preparing review…' : 'Review your plan →'}
                    </button>
                  </div>
                </div>
              </>
            )}

            {((!locked && saved.step === 2) || payment) && selected && (
              <>
                <h2 ref={title} tabIndex={-1}>
                  Everything look right?
                </h2>
                <p className="ga-subtitle">
                  {payment && !preview && (order?.status === 'creating_checkout' || startedCheckout)
                    ? 'Preparing your secure Stripe checkout…'
                    : 'Check your SIMs and total before payment.'}
                </p>
                <div className="ga-review-email">
                  <span>Account email</span>
                  <strong>{saved.email}</strong>
                  {!payment && (
                    <button onClick={() => setSaved((s) => ({ ...s, step: 0, emailRoute: null }))}>Edit</button>
                  )}
                </div>
                <div className="ga-review-list">
                  {saved.items.map((sim, i) => (
                    <div key={sim.iccid}>
                      <SimIcon small />
                      <span>
                        <strong>{sim.nickname || `Camera ${i + 1}`}</strong>
                        <small>
                          {sim.sim_type === 'regular' ? 'Regular UK SIM' : 'Solar SIM'} ending{' '}
                          {sim.iccid.slice(-4)}
                          {sim.esim_batch ? ' · eSIM setup needed' : ''}
                        </small>
                      </span>
                    </div>
                  ))}
                </div>
                <div className="ga-review-total">
                  <span>
                    {selected.name} · {count} × {money(selected.unit_amount)} / {selected.interval}
                    {prepaid ? ` × ${saved.prepayYears} ${saved.prepayYears === 1 ? 'year' : 'years'}` : ''}
                  </span>
                  <strong>
                    {money(selected.unit_amount * count * (prepaid ? saved.prepayYears : 1))}
                    <small> {prepaid ? 'upfront' : `/ ${selected.interval}`}</small>
                  </strong>
                  <p>{prepaid ? `Including VAT. Covers ${saved.prepayYears} ${saved.prepayYears === 1 ? 'year' : 'years'}; then renews at the normal annual price.`
                    : 'Including VAT. Renews automatically on the same date for all SIMs.'}</p>
                  <p className="ga-review-pool">
                    <strong>{selected.data_gb * count} GB pooled each month</strong> · about{' '}
                    {selected.data_gb * count}–{selected.data_gb * count * 2} hours of shared streaming
                  </p>
                  {!prepaid && selected.interval === 'year' && annualSaving(selected) > 0 && (
                    <p className="ga-review-saving">
                      You save {money(annualSaving(selected) * count)} a year by paying yearly
                      instead of monthly.
                    </p>
                  )}
                </div>
                {!payment && count > 1 && (
                  <p className="ga-review-note">
                    These SIMs share one plan and renewal date and can’t be paused separately.
                    Contact support to change or pause the group or remove a SIM.
                    {selected.interval === 'year' && count >= 3 &&
                      ' If fewer than three SIMs remain, standard pricing starts at the next renewal.'}
                  </p>
                )}
                {!payment ? (
                  <div className="ga-footer">
                    <button
                      className="ga-back"
                      disabled={busy || !ready}
                      onClick={() => run(changePlan, 'Loading all plan options…')}
                    >
                      ← Change plan
                    </button>
                    <button
                      className="ga-button"
                      disabled={busy || !checkoutEnabled}
                      onClick={() => {
                        run(async () => {
                          const current = await api(
                            `/orders/${saved.orderId}/checkout`,
                            'POST',
                            {},
                            saved.token
                          );
                          setOrder(current);
                          if (!preview) setStartedCheckout(true);
                        }, 'Preparing secure checkout…');
                      }}
                    >
                      {busy
                        ? 'Preparing…'
                        : preview
                          ? 'Continue to test payment →'
                          : checkoutEnabled
                            ? 'Continue to secure payment →'
                            : 'Payment disabled during pilot testing'}
                    </button>
                  </div>
                ) : (
                  <div className="ga-review-checkout">
                    {!preview && (order?.status === 'creating_checkout' || startedCheckout) && (
                      <div className="ga-working" role="status" aria-live="polite">
                        <span className="ga-spinner" aria-hidden="true" />
                        {order?.status === 'creating_checkout'
                          ? 'Checking your SIMs and price…'
                          : 'Opening Stripe…'}
                      </div>
                    )}
                    {preview && <p>No card details needed. This is a simulated payment.</p>}
                    {preview && (
                      <div className="ga-payment-actions">
                        <button
                          className="ga-button"
                          disabled={busy || order?.status !== 'checkout'}
                          onClick={() =>
                            run(async () =>
                              setOrder(
                                await api(
                                  `/orders/${saved.orderId}/preview-payment`,
                                  'POST',
                                  { outcome: 'success' },
                                  saved.token
                                )
                              )
                            )
                          }
                        >
                          Simulate successful payment
                        </button>
                        <button
                          className="ga-button ga-button-secondary"
                          disabled={busy || order?.status !== 'checkout'}
                          onClick={() =>
                            run(async () => {
                              await api(
                                `/orders/${saved.orderId}/preview-payment`,
                                'POST',
                                { outcome: 'decline' },
                                saved.token
                              );
                            })
                          }
                        >
                          Try a declined payment
                        </button>
                      </div>
                    )}
                    <div className="ga-footer">
                      <button
                        className="ga-back"
                        disabled={busy || !ready || order?.status === 'creating_checkout'}
                        onClick={() => run(changePlan, 'Loading all plan options…')}
                      >
                        ← Change plan
                      </button>
                      {!preview && order?.checkout_url && (
                        <a className="ga-button" href={order.checkout_url}>
                          Continue to secure payment →
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}

            {progress && (
              <>
                <div
                  className={`ga-status-icon ${order?.status === 'complete' ? 'ga-status-done' : ''}`}
                >
                  {order?.status === 'complete' ? '✓' : '◷'}
                </div>
                <h2 ref={title} tabIndex={-1}>
                  {order?.status === 'complete'
                    ? count === 1 ? 'Your SIM is activated' : 'Your SIMs are activated'
                    : order?.status === 'refund_pending'
                      ? 'We’re refunding this payment'
                    : order?.status === 'needs_attention'
                      ? 'We’re checking your activation'
                      : order?.status === 'resolved'
                        ? 'Your activation has been updated'
                        : count === 1 ? 'Payment received. Activating your SIM…' : 'Payment received. Activating your SIMs…'}
                </h2>
                <p className="ga-subtitle">
                  {order?.status === 'complete'
                    ? 'Activation can take up to 24 hours. Switch your cameras off and back on — they usually come online within 5 minutes.'
                    : order?.status === 'refund_pending'
                      ? 'A SIM was paid for in another checkout. No SIMs from this payment will be activated. Please don’t pay again.'
                    : order?.status === 'resolved' && order.items.every((sim) => sim.state === 'refunded')
                      ? 'This payment was refunded. No SIMs from this checkout were activated.'
                    : 'You can leave this page open to follow progress. Please don’t pay again.'}
                </p>
                <div className="ga-progress-list" aria-live="polite">
                  {order?.items.map((sim, i) => (
                    <div key={sim.iccid}>
                      <span className={`ga-progress-dot ga-progress-${sim.state}`}>
                        {sim.state === 'active' ? '✓' : sim.state === 'needs_attention' ? '!' : '·'}
                      </span>
                      <div>
                        <strong>{sim.nickname || `Camera ${i + 1}`}</strong>
                        <small>SIM ending {sim.iccid.slice(-4)}</small>
                        <p>{sim.message || 'Waiting to activate'}</p>
                      </div>
                      <span className="ga-state-label">
                        {sim.state === 'active'
                          ? 'Activated'
                          : sim.state === 'needs_attention'
                            ? 'With support'
                            : sim.state === 'refunded'
                              ? 'Refunded'
                              : 'In progress'}
                      </span>
                    </div>
                  ))}
                </div>
                {(prepaid ? order?.prepay_paid_until : order?.renewal_at) && (
                  <p className="ga-note">
                    {prepaid ? 'Normal annual billing resumes: ' : 'Next renewal: '}
                    {new Date((prepaid ? order!.prepay_paid_until! : order!.renewal_at!) * 1000).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric'
                    })}
                  </p>
                )}
                {order?.items.some((i) => i.esim_batch) && (
                  <div className="ga-esim">
                    <strong>A quick setup step for your eSIM</strong>
                    <p>
                      SIMs ending{' '}
                      {order.items
                        .filter((i) => i.esim_batch)
                        .map((i) => i.iccid.slice(-4))
                        .join(', ')}{' '}
                      need a one-time camera setting update.
                    </p>
                    <a
                      href="https://www.farmstream.co.uk/pages/esim-manual-update"
                      target="_blank"
                      rel="noreferrer"
                    >
                      View eSIM update instructions ↗
                    </a>
                  </div>
                )}
                <p className="ga-hint">Activation reference: {order?.id}</p>
                {preview && order?.status === 'needs_attention' && (
                  <p className="ga-note">
                    <a href="/team/group-activations" target="_blank" rel="noreferrer">
                      Open the test team recovery tools ↗
                    </a>
                  </p>
                )}
                {preview ? (
                  <button className="ga-button ga-payment-back" onClick={startOver} disabled={busy}>
                    Start a fresh test
                  </button>
                ) : (
                  <a className="ga-button ga-payment-back" href="/">
                    Sign in to your dashboard →
                  </a>
                )}
              </>
            )}
          </section>

          {progress && (
            <aside className="ga-aside">
              <div className="ga-summary">
                <h3>Your plan</h3>
                <div className="ga-summary-line">
                  <span>Region</span>
                  <strong>United Kingdom</strong>
                </div>
                <div className="ga-summary-line">
                  <span>SIM type</span>
                  <strong>{simTypeLabel}</strong>
                </div>
                {billingPlan && (
                  <>
                    <div className="ga-summary-line">
                      <span>Plan</span>
                      <strong>{billingPlan.name}</strong>
                    </div>
                    <div className="ga-summary-line">
                      <span>Per SIM / {billingPlan.interval}</span>
                      <strong>{money(billingPlan.unit_amount)}</strong>
                    </div>
                    <div className="ga-summary-line">
                      <span>Pooled data / month</span>
                      <strong>{billingPlan.data_gb * billingCount} GB</strong>
                    </div>
                    <div className="ga-summary-line">
                      <span>Shared streaming</span>
                      <strong>
                        ≈{billingPlan.data_gb * billingCount}–
                        {billingPlan.data_gb * billingCount * 2} hours
                      </strong>
                    </div>
                    <div className="ga-summary-total">
                      <span>
                        {prepaid ? 'Paid upfront' : progress ? 'At next renewal' : `Total / ${billingPlan.interval}`}
                      </span>
                      <strong>{money(billingPlan.unit_amount * billingCount * (prepaid ? (order?.prepay_years || saved.prepayYears) : 1))}</strong>
                    </div>
                    <small>Including VAT</small>
                    {!prepaid && progress && billingCount === 1 && billingPlan.interval === 'year' && (
                      <p className="ga-note">
                        One SIM remains. Standard pricing applies at the next renewal.
                      </p>
                    )}
                  </>
                )}
                {!selected && (
                  <div className="ga-from">
                    <span>Yearly group plans from</span>
                    <strong>
                      £50<small> / SIM / year</small>
                    </strong>
                    <small>Including VAT · when activating 2+ SIMs</small>
                  </div>
                )}
                <ul>
                  {prepaid && billingCount === 1 ? <>
                    <li><span>✓</span> {order?.prepay_years || saved.prepayYears} {(order?.prepay_years || saved.prepayYears) === 1 ? 'year' : 'years'} paid upfront</li>
                    <li><span>✓</span> Data renews each month</li>
                    <li><span>✓</span> Normal annual billing after your prepaid term</li>
                  </> : <>
                    <li><span>✓</span> One checkout for all your SIMs</li>
                    <li><span>✓</span> One shared renewal date</li>
                    <li><span>✓</span> Every camera in your account</li>
                  </>}
                </ul>
              </div>
            </aside>
          )}
        </div>
        <StreamingProviderNotice className="ga-provider-notice" />
      </main>
    </div>
  );
};
